// Theme management for the marketing site.
// Drives BOTH the site's own `[data-theme]` tokens and Bootstrap's native
// `[data-bs-theme]` so built-in components follow along.
//
// The stored value is a PREFERENCE: "system" | "light" | "dark". "system"
// resolves to the OS scheme (and re-resolves live when the OS changes). Dark
// remains the default experience for visitors who never chose.

const THEME_KEY = "theme";

export const themes = {
  SYSTEM: "system",
  LIGHT: "light",
  DARK: "dark",
};

export const getStoredTheme = () => {
  try {
    const stored = localStorage.getItem(THEME_KEY);
    return stored === themes.LIGHT ||
      stored === themes.DARK ||
      stored === themes.SYSTEM
      ? stored
      : themes.DARK;
  } catch {
    return themes.DARK;
  }
};

export const setStoredTheme = (theme) => {
  try {
    localStorage.setItem(THEME_KEY, theme);
  } catch {
    // Ignore storage failures (private mode, etc.)
  }
};

const resolveTheme = (preference) => {
  if (preference === themes.LIGHT) return themes.LIGHT;
  if (preference === themes.DARK) return themes.DARK;
  // system
  try {
    return window.matchMedia("(prefers-color-scheme: light)").matches
      ? themes.LIGHT
      : themes.DARK;
  } catch {
    return themes.DARK;
  }
};

export const applyTheme = (preference) => {
  const pref = Object.values(themes).includes(preference)
    ? preference
    : themes.DARK;
  const resolved = resolveTheme(pref);
  const el = document.documentElement;
  el.setAttribute("data-theme", resolved);
  el.setAttribute("data-bs-theme", resolved);
  setStoredTheme(pref);
  return pref;
};

// Kept for compatibility: flips between explicit light/dark.
export const toggleTheme = () => {
  const current =
    document.documentElement.getAttribute("data-theme") || themes.DARK;
  return applyTheme(current === themes.DARK ? themes.LIGHT : themes.DARK);
};

export const initializeTheme = () => {
  const pref = applyTheme(getStoredTheme());

  // Follow OS scheme changes live while the preference is "system".
  try {
    window
      .matchMedia("(prefers-color-scheme: light)")
      .addEventListener("change", () => {
        if (getStoredTheme() === themes.SYSTEM) applyTheme(themes.SYSTEM);
      });
  } catch {
    // matchMedia listeners unsupported — static resolution is fine.
  }

  return pref;
};
